import { memo, useMemo } from 'react'
import _ from 'lodash'
import {
  Button,
  Title,
  Card,
  CardBody,
  ModalProvider,
  classNames,
} from '@panwds/react-ui'
import { getFrameworkVar } from '@sparky/framework'
import CardHeader from './CardHeader'
import AppLogo from '../common/AppLogo'
import { useAppInfoModule } from './AppInfo'
import { ALink, PrimaryButton } from '../common/Common'
import { showRenameInstance } from '../../utils/common'
import IconMenu from './IconMenu'
import './MoreApps.scss'
import type { VFC } from 'react'
import type { AppManifest, ReadonlyTsgInstances, Instance } from '@sparky/framework'
import type { InstancesAndApps, AppFilters, AppAccessFilterFn } from '../../types'
import type { IconMenuItem } from './IconMenu'
import { TSG_NONE } from '../../constants'

const APP_DETAILS_PREFIX = '/hub/products/'
const PRODUCT_INFO_ACTION = 'app-info'
const RENAME_INSTANCE_ACTION = 'rename-instance'

type AppProductTileProps = {
  app: AppManifest
  tsgId?: string
  instance?: Instance
  appAccessFilterFn: AppAccessFilterFn
}
const AppProductTile: VFC<AppProductTileProps> = ({ tsgId, app, instance, appAccessFilterFn }) => {
  const url = useMemo(() => {
    if (app.marketplace_content) {
      const { url, product_link, url_name, body } = app.marketplace_content
      if (url) {
        return url
      }
      if (product_link && !body) {
        return product_link
      }
      if (url_name) {
        return `${APP_DETAILS_PREFIX}${url_name}`
      }
    }
    return `${APP_DETAILS_PREFIX}${app.app_id}`
  }, [app.app_id, app.marketplace_content])

  const hasButton = tsgId === TSG_NONE.id ? Boolean(app.setup?.unified_activation_path) : appAccessFilterFn(app)
  const primaryBtn = hasButton && <PrimaryButton instance={instance} app={app} />

  const logo = <AppLogo src={app.logo || app.normalized_logo_url} alt={' '} />

  const learnMoreLink = <ALink to={url} className='sparky-hub-app-tile-btn' dataTestId={`hub-learn-more:${app.app_id}`}>
    <Button appearance='clear' size='md'>Learn More</Button>
  </ALink>

  const { openModal } = useAppInfoModule(instance, app)
  const testId = (app.is_panw ? 'panw-app:' : 'partner-app:') + app.app_id

  const [title, actions] = useMemo(() => {
    const title = <span className='cm-grow cm-min-w-[13rem]'>{app.display_name}</span>
    if (!instance) {
      return [title]
    }
    const menu = [
      {
        label: 'Product Information',
        value: PRODUCT_INFO_ACTION,
        onClick: openModal,
      },
      ...app?.flags?.is_rename_allowed ? [{
        label: 'Rename',
        value: RENAME_INSTANCE_ACTION,
        onClick: () => {
          showRenameInstance(instance?.tenant_id)
        },
      }] : [],
    ] as IconMenuItem[]
    const menuBtn = hasButton && <IconMenu className='cm-grow-0 cm-bg-white hover:cm-bg-gray-200 dark:cm-bg-transparent dark:hover:cm-bg-gray-900' iconMenuList={menu} />
    return [title, menuBtn]
  }, [hasButton, instance, openModal, app.display_name, app?.flags?.is_rename_allowed])

  return <Card
    addClassName='sparky-hub-app-tile hover:cm-border-blue-600'
    dataTestId={testId}
    data-app-id={instance?.app_id || app.app_id}
    data-display-app-id={app.app_id}
  >
    <CardHeader
      appearance='clear'
      icon={logo}
      title={title}
      subTitle={app.description || undefined}
      actions={actions}
    />
    <CardBody addClassName='sparky-hub-app-tile-actions'>
      {primaryBtn}
      {learnMoreLink}
    </CardBody>
  </Card>
}

type AppProductsProps = { apps: AppManifest[], instances: ReadonlyTsgInstances, title: string, addClassName: string, appAccessFilterFn: AppAccessFilterFn }
const AppProducts: VFC<AppProductsProps> = ({ apps, title, instances, addClassName, appAccessFilterFn }) => {
  const renderred = useMemo(() => {
    if (!apps.length) {
      return []
    }
    const mapping = instances.reduce((o, inst) => {
      o[inst.app_launch_display_override || inst.app_id_display_override || inst.app_id] = inst
      return o
    }, {} as Record<string, Instance>)
    const merge_oneapp_tiles = getFrameworkVar('merge_oneapp_tiles') &&
      (!instances.length || (instances as any).flags?.is_oneapp_ready)
    const replacements = apps.reduce((mapping, app) => {
      if (app.extra?.merge_tiles_for_tsg_and_public?.length) {
        if (merge_oneapp_tiles) {
          for (const appId of app.extra.merge_tiles_for_tsg_and_public) {
            if (appId !== app.app_id) {
              mapping[appId] = app
            }
          }
        }
        else {
          mapping[app.app_id] = app
        }
      }
      return mapping
    }, {} as Record<string, AppManifest>)
    return apps.filter((app) =>
      !replacements[app.app_id]
    ).map((app) => <AppProductTile
      key={app.app_id}
      app={app}
      instance={mapping[app.app_id]}
      tsgId={instances.tsg_id}
      appAccessFilterFn={appAccessFilterFn}
    />)
  }, [apps, instances, appAccessFilterFn])

  if (!renderred.length) {
    return null
  }

  const className = classNames('sparky-hub-section', addClassName)

  return <section className={className}>
    <Title level={2} addClassName='sparky-hub-section-title'>{title}</Title>
    <div className='sparky-hub-app-tiles'>
      {renderred}
    </div>
  </section>
}

const MoreApps: VFC<InstancesAndApps & AppFilters> = ({ apps, instances, appFilterFn, appAccessFilterFn }) => {
  const [panwApps, partnerApps] = useMemo(() => {
    const filtered = apps.filter(appFilterFn)
    const sorted = _.orderBy(filtered, ['display_name'])
    return _.partition(sorted, app => app.is_panw)
  }, [apps, appFilterFn])
  return <div className='sparky-hub-more-apps'>
    <ModalProvider>
      <AppProducts apps={panwApps} title='Explore Apps from Palo Alto Networks' addClassName='sparky-hub-panw-apps' instances={instances} appAccessFilterFn={appAccessFilterFn} />
      <AppProducts apps={partnerApps} title='Explore Apps from Partners for Strata Logging Service' addClassName='sparky-hub-partner-apps' instances={instances} appAccessFilterFn={appAccessFilterFn} />
    </ModalProvider>
  </div>
}

export default memo(MoreApps)
